<template>
  <div class="min-h-full flex flex-col bg-growth-25">
  <PageHeader title="Engage">
    <template v-if="tabs.length > 1" #bottom>
      <Page_Tabs :tabs="tabs" no-border class="px-6 pt-4" />
    </template>
  </PageHeader>

  <div class="flex-1 flex flex-col">
  <template v-if="activeConfigTab">
    <ConfigurablePage
      v-if="engageConfig"
      :page-identifier="configPage"
      :config="engageConfig"
      :tab="activeConfigTab"
      :default-tab="defaultConfigTab"
      :show-page-header="false"
    />
    <div v-else class="min-h-screen flex">
      <LoadingIndicator message="Loading page configuration..." />
    </div>
  </template>

  <EngageHome v-else-if="activeTab === 'home'" />

  <TransformOps v-else-if="activeTab === 'transform'" />

  <div v-else-if="activeTab === 'projects'" class="p-6">
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <ComponentHeader title="Project Management" :heading-level="4" />
      <div class="p-6">
        <Project_Table :grouper-columns="['project_name','action_name','appuser_name','practice_name']" />
      </div>
    </div>
  </div>

  <div v-else-if="activeTab === 'intelligence'" class="flex-1 flex flex-col p-6">
    <div class="flex-1 min-h-[500px] flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden">
      <AnalystPanel />
    </div>
  </div>

  <div v-else-if="activeTab === 'surveys'" class="p-6">
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <ComponentHeader title="Survey Summary" :heading-level="4" />
      <div class="p-6">
        <Page_Tabs
          :tabs="['Response Overview', 'LLM Summaries']"
          :initial-tab="surveyTab === 'aggregate' ? 'Response Overview' : 'LLM Summaries'"
          @select="surveyTab = $event === 'Response Overview' ? 'aggregate' : 'details'"
        />
        <div class="mt-4">
          <SurveyAggregate v-if="surveyTab === 'aggregate'" />
          <SurveyQuestionSummaryTable v-else :auto-height="true" />
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="activeTab === 'network-map'" class="p-6">
    <NetworkMap />
  </div>

  <div v-else-if="activeTab === 'rostering'" class="p-6">
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <ProviderRosterReport />
    </div>
  </div>
  </div>
  </div>
</template>

<script>
import axios from 'axios';
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import ConfigurablePage from '@/components/ConfigurablePage.vue';
import Project_Table from '@/components/Lists/Project_Table.vue';
import AnalystPanel from '@/components/PersonalAnalyst/AnalystPanel.vue';
import SurveyQuestionSummaryTable from '@/components/Lists/SurveyQuestionSummary_Table.vue';
import SurveyAggregate from '@/components/Lists/SurveyAggregate.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { canAccessRoute } from '@/utils/routeAccess';
import { sectionTab, visibleSections } from '@/utils/pageSections';
import { ENGAGE_TABS, ENGAGE_CONFIG_PAGE, ENGAGE_DEFAULT_CONFIG_TAB } from '@/components/Engage/engage_tabs';

const EngageHome = defineAsyncComponent(() => import('@/components/Engage/EngageHome.vue'));
const TransformOps = defineAsyncComponent(() => import('@/components/Transform/TransformOps.vue'));
const NetworkMap = defineAsyncComponent(() => import('@/components/NetworkMap/NetworkMap.vue'));
const ProviderRosterReport = defineAsyncComponent(() => import('@/components/Rostering/ProviderRosterReport.vue'));

export default {
  name: 'Engage_Overview',
  components: {
    ConfigurablePage,
    EngageHome,
    TransformOps,
    Project_Table,
    AnalystPanel,
    SurveyQuestionSummaryTable,
    SurveyAggregate,
    PageHeader,
    ComponentHeader,
    Page_Tabs,
    LoadingIndicator,
    NetworkMap,
    ProviderRosterReport,
  },
  props: {
    activeTab: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      surveyTab: 'aggregate',
      engageConfig: null,
      configPage: ENGAGE_CONFIG_PAGE,
      defaultConfigTab: ENGAGE_DEFAULT_CONFIG_TAB,
    };
  },
  computed: {
    ...mapGetters('tenant', ['current_tenant_roles']),
    populatedConfigTabs() {
      if (!this.engageConfig) return null;
      return new Set(
        visibleSections(this.engageConfig.layout_config, this.current_tenant_roles)
          .map((section) => sectionTab(section, ENGAGE_DEFAULT_CONFIG_TAB))
      );
    },
    reachableTabs() {
      return ENGAGE_TABS.filter((tab) => (
        canAccessRoute(tab.path, this.$router)
        && (!tab.configTab || !this.populatedConfigTabs || this.populatedConfigTabs.has(tab.configTab))
      ));
    },
    tabs() {
      return this.reachableTabs
        .filter((tab) => !tab.configTab || this.populatedConfigTabs)
        .map((tab) => ({ label: tab.label, to: tab.path }));
    },
    activeConfigTab() {
      return ENGAGE_TABS.find((tab) => tab.key === this.activeTab)?.configTab || null;
    },
    activeConfigTabIsEmpty() {
      return !!(this.activeConfigTab && this.populatedConfigTabs && !this.populatedConfigTabs.has(this.activeConfigTab));
    },
  },
  watch: {
    activeConfigTabIsEmpty(empty) {
      if (empty && this.reachableTabs.length) {
        this.$router.replace(this.reachableTabs[0].path);
      }
    },
  },
  async created() {
    const { data } = await axios.get(`/api/page-configurations/by-identifier/${ENGAGE_CONFIG_PAGE}/`);
    this.engageConfig = data;
  },
};
</script>
